import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './Metrics.css';

// SVG Icons
const icons = {
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  naira: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  mapPin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
};

// Animated Counter Hook
const useCounter = (end, duration = 2000, inView) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!inView) return;
    
    let startTime;
    let animationFrame;
    const startValue = 0;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(startValue + (end - startValue) * easeOutQuart);
      
      setCount(currentCount);
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, inView]);
  
  return count;
};

const MetricCard = ({ metric, index, inView }) => {
  const count = useCounter(metric.value, 2000, inView);
  
  return (
    <motion.div 
      className="metric-card"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      whileHover={{ y: -8, scale: 1.02 }}
    >
      <div className="metric-icon-wrapper">
        <div className="metric-icon">{icons[metric.icon]}</div>
      </div>
      <h3 className="metric-number">
        {metric.prefix}{count.toLocaleString()}{metric.suffix}
      </h3>
      <h4 className="metric-label">{metric.label}</h4>
      <p className="metric-description">{metric.description}</p>
    </motion.div>
  );
};

const Metrics = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  
  const metrics = [
    {
      value: 5000,
      prefix: '',
      suffix: '+',
      label: 'Active Retailers',
      description: 'Businesses served nationwide',
      icon: 'users',
    },
    {
      value: 8.5,
      prefix: '₦',
      suffix: 'B',
      label: 'Products Distributed',
      description: 'Annual distribution value',
      icon: 'naira',
    },
    {
      value: 150,
      prefix: '',
      suffix: '+',
      label: 'Cities Covered',
      description: 'Nationwide delivery network',
      icon: 'mapPin',
    },
    {
      value: 98,
      prefix: '',
      suffix: '%',
      label: 'Customer Satisfaction',
      description: 'Rated by our partners',
      icon: 'star',
    },
  ];

  return (
    <section className="metrics" ref={ref}>
      <div className="container">
        <div className="metrics-grid">
          {metrics.map((metric, index) => (
            <MetricCard 
              key={index} 
              metric={metric} 
              index={index}
              inView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Metrics;
